import { Controller } from "@hotwired/stimulus";

// Map for a coordinate. In form mode it reads the latitude/longitude inputs
// and drops a marker, recentring as they change; clicking the map writes the
// coordinates back into the inputs. Without inputs it is a read-only display
// fed by the latitude/longitude values. Uses OpenStreetMap tiles (no API key)
// and a circle marker so no image assets are needed.
//
// Leaflet is imported dynamically, so the library and its tiles are fetched by
// the pages that actually carry a map — and on those, only once the reader has
// scrolled near it. Statically imported it rode in the one application bundle,
// which meant every page on the site paid for a map it never draws.
//
//   <details data-controller="map" data-action="toggle->map#reveal">
//     <input data-map-target="latitude"  data-action="input->map#update">
//     <input data-map-target="longitude" data-action="input->map#update">
//     <div data-map-target="canvas" class="h-72"></div>
//   </details>
//
//   <div data-controller="map" data-map-latitude-value="54.5" data-map-longitude-value="15.2">
//     <div data-map-target="canvas" class="h-64"></div>
//     <button data-map-target="release" data-action="map#release">Haritayı hareket ettir</button>
//   </div>
export default class extends Controller {
  static targets = ["latitude", "longitude", "canvas", "release"];
  static values = {
    zoom: { type: Number, default: 5 },
    latitude: Number,
    longitude: Number,
  };

  connect() {
    // A form needs its map the moment the section opens, and it opens on demand
    // rather than by scrolling; a page's map waits until the reader is near it.
    if (this.hasLatitudeTarget) {
      this.load();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.stopWatching();
          this.load();
        }
      },
      { rootMargin: "300px" },
    );
    this.observer.observe(this.element);
  }

  disconnect() {
    this.stopWatching();
    this.map?.remove();
    this.map = null;
    this.loading = null;
  }

  // Idempotent: the observer, a details toggle and a value change can all ask
  // for the map, and only the first one builds it.
  load() {
    this.loading ||= this.build();
    return this.loading;
  }

  async build() {
    const { default: L } = await import("leaflet");
    if (!this.element.isConnected) return;

    // A finger is not a mouse: a map that pans under a thumb swallows the swipe
    // that was meant to scroll the page past it. On a touch screen the display
    // map starts inert and `release` hands it over. The form's map is a picker —
    // it has to stay live, whatever it is being pointed at with.
    const inert = L.Browser.mobile && !this.hasLatitudeTarget;

    this.map = L.map(this.canvasTarget, {
      scrollWheelZoom: false,
      dragging: !inert,
      touchZoom: !inert,
      zoomControl: false,
    }).setView([20, 0], 1);

    // Leaflet names its zoom buttons in English; the interface is Turkish, and
    // these titles are the buttons' accessible names.
    L.control
      .zoom({ zoomInTitle: "Yakınlaştır", zoomOutTitle: "Uzaklaştır" })
      .addTo(this.map);

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap katkıda bulunanlar",
      maxZoom: 19,
    }).addTo(this.map);

    if (this.hasLatitudeTarget) {
      this.map.on("click", (event) =>
        this.write(event.latlng.lat, event.latlng.lng),
      );
    }

    this.leaflet = L;
    if (inert && this.hasReleaseTarget) {
      this.releaseTarget.classList.remove("hidden");
    }

    this.render();
  }

  // The reader asked for the map itself; give it the handlers back and drop the
  // button, which has nothing left to say.
  release() {
    this.map?.dragging.enable();
    this.map?.touchZoom.enable();
    this.releaseTarget.classList.add("hidden");
  }

  update() {
    this.render();
  }

  // Leaflet mis-measures when it inits inside a closed <details>; re-measure
  // once the section is revealed.
  async reveal() {
    if (!this.element.open) return;
    await this.load();
    this.map?.invalidateSize();
    this.render();
  }

  render() {
    if (!this.map) return;

    const lat = this.hasLatitudeTarget
      ? parseFloat(this.latitudeTarget.value)
      : this.latitudeValue;
    const lng = this.hasLongitudeTarget
      ? parseFloat(this.longitudeTarget.value)
      : this.longitudeValue;
    if (!this.valid(lat, lng)) {
      this.marker?.remove();
      this.marker = null;
      return;
    }
    this.setMarker(lat, lng);
    this.map.setView([lat, lng], Math.max(this.map.getZoom(), this.zoomValue));
  }

  // Map click: sync the inputs (dispatching input so #update re-renders).
  write(lat, lng) {
    this.latitudeTarget.value = lat.toFixed(6);
    this.longitudeTarget.value = lng.toFixed(6);
    this.latitudeTarget.dispatchEvent(new Event("input", { bubbles: true }));
    this.longitudeTarget.dispatchEvent(new Event("input", { bubbles: true }));
  }

  setMarker(lat, lng) {
    if (this.marker) {
      this.marker.setLatLng([lat, lng]);
    } else {
      this.marker = this.leaflet
        .circleMarker([lat, lng], {
          radius: 8,
          color: "#1e40af",
          weight: 2,
          fillColor: "#2563eb",
          fillOpacity: 0.9,
        })
        .addTo(this.map);
    }
  }

  stopWatching() {
    this.observer?.disconnect();
    this.observer = null;
  }

  valid(lat, lng) {
    return (
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
    );
  }
}
